import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AccountSessionUser {
  uuid: string;
  name: string;
  email: string;
  forgotAccount?: boolean;
  loginUuid?: string;
  roles?: string[];
  permissions?: string[];
}

export interface AccountSession {
  user: AccountSessionUser;
  createdAt: string;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  details?: string[];
  data?: T;
  login?: {
    uuid?: string;
  };
}

export interface WhitelistRole {
  uuid: string;
  name: string;
}

export interface WhitelistEntry {
  uuid: string;
  email: string;
  roles: WhitelistRole[];
  created_at?: string;
  updated_at?: string;
}

export interface WhitelistListResponse {
  success: boolean;
  data?: WhitelistEntry[];
  count?: number;
  page?: number;
  pageSize?: number;
  availableRoles?: WhitelistRole[];
  message?: string;
  error?: string;
}

export interface ValidationSurveySummary {
  sentAt?: string | null;
  opened?: boolean | null;
  openedAt?: string | null;
  q1?: boolean | null;
  q2?: number | null;
  q3?: boolean | null;
  q4?: boolean | null;
  q5?: number | null;
  q6?: string | null;
}

export interface ValidationBetaTesterSummary {
  applied: boolean;
  withdrawn: boolean;
  appliedAt?: string | null;
}

export interface ValidationEmailEntry {
  id: string;
  email: string;
  created?: string;
  subscribed?: boolean | null;
  survey: ValidationSurveySummary;
  betaTester: ValidationBetaTesterSummary;
  whitelisted: boolean;
  whitelistUuid?: string | null;
}

export interface ValidationEmailListResponse {
  success: boolean;
  message?: string;
  data?: {
    entries?: ValidationEmailEntry[];
    count?: number;
    page?: number;
    pageSize?: number;
  };
}

export interface ClientAccountPlan {
  name: string;
  reqs: number;
}

export interface ClientAccount {
  uuid: string;
  name: string;
  email: string;
  verified: boolean;
  monthly_plan_id?: number | null;
  monthly_plan?: ClientAccountPlan | null;
  reqs?: number | null;
  monthly_reqs?: number | null;
  cycle_start_at?: string | null;
  joined?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  roles?: WhitelistRole[];
}

export interface ClientAccountListResponse {
  success: boolean;
  data?: ClientAccount[];
  count?: number;
  page?: number;
  pageSize?: number;
  availableRoles?: WhitelistRole[];
  message?: string;
  error?: string;
}

export interface CatalogPermission {
  uuid: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export interface CatalogRole {
  uuid: string;
  name: string;
  permissions: CatalogPermission[];
  created_at?: string;
  updated_at?: string;
}

export interface PermissionListResponse {
  success: boolean;
  data?: CatalogPermission[];
  count?: number;
  page?: number;
  pageSize?: number;
  message?: string;
  error?: string;
}

export interface RoleListResponse {
  success: boolean;
  data?: CatalogRole[];
  count?: number;
  page?: number;
  pageSize?: number;
  availablePermissions?: CatalogPermission[];
  message?: string;
  error?: string;
}

export interface AccountPayload {
  uuid?: string;
  name?: string;
  forgot_account?: boolean;
  login_uuid?: string;
  roles?: string[];
  permissions?: string[];
}

export interface AccountInfoPayload {
  authenticated?: boolean;
  callerRoles?: string[];
  roleCatalog?: Record<string, { permissions?: string[]; description?: string }>;
}

export interface AccountDashboardLink {
  role: string;
  label: string;
  path: string;
}

export const ACCOUNT_DASHBOARDS: AccountDashboardLink[] = [
  {
    role: 'account_self_service',
    label: 'Self-Service',
    path: '/account/self-service',
  },
  {
    role: 'user',
    label: 'Encryption',
    path: '/account/encryption',
  },
  {
    role: 'admin',
    label: 'Admin',
    path: '/account/admin',
  },
];

export interface AccountRecord {
  uuid: string;
  name: string;
  email: string;
  verified: boolean;
}

export interface PlanUpdateResult {
  plan: {
    id: number;
    name: string;
    reqs: number;
  };
  clients: Array<{
    uuid: string;
    monthly_plan: number;
    plan_name: string;
    plan_reqs: number;
  }>;
}

export interface CreatedApiKey {
  id: string;
  apiKey: string;
  expiresAt: string;
  clientId: string;
  rotated: boolean;
  previousActiveKeysRevoked: Array<{
    id: string;
    description?: string | null;
  }>;
  storeKeyMessage: string;
}

export interface ApiKeySummary {
  apiKey: {
    id: string;
    description?: string | null;
    status: string;
    createdAt: string;
    expiresAt: string;
    lastUsedAt?: string | null;
    usageCount: number;
  } | null;
  plan: {
    id: number | null;
    name: string | null;
    cryptsPerMonth: number | null;
  };
  usage: {
    cryptsUsedThisMonth: number;
    cryptsRemainingThisMonth: number | null;
    cycleStartAt: string | null;
    monthRollsOverAt: string | null;
  };
}

export interface DestructiveOpsPasswordStatus {
  hasDestructivePermissions: boolean;
  hasCredential: boolean;
  requiresSetup: boolean;
  requiresRotation: boolean;
  expired: boolean;
  expiresAt: string | null;
  daysUntilExpiry: number | null;
  passwordRotatedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class AccountAuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storageKey = 'hebern.account.session';
  private readonly sessionSignal = signal<AccountSession | null>(null);

  readonly session = this.sessionSignal.asReadonly();
  readonly isAuthenticated = computed(
    () => !!this.sessionSignal()?.user?.uuid
  );
  readonly user = computed(() => this.sessionSignal()?.user ?? null);
  readonly roles = computed(() => this.sessionSignal()?.user.roles ?? []);
  readonly permissions = computed(
    () => this.sessionSignal()?.user.permissions ?? []
  );
  readonly dashboards = computed(() => {
    const roles = new Set(this.roles());
    return ACCOUNT_DASHBOARDS.filter((dashboard) => roles.has(dashboard.role));
  });

  constructor() {
    this.restoreSession();
  }

  createAccount(body: { name: string; email: string; password: string }) {
    return this.post<AccountRecord>('/api/v1/accounts', body).pipe(
      tap(() => this.rememberPendingEmail(body.email))
    );
  }

  verifyAccount(email: string, passcode: string) {
    return this.post<AccountPayload>('/api/v1/accounts/verify', {
      email,
      passcode,
    }).pipe(tap((response) => this.storeSessionFromResponse(response, email)));
  }

  login(email: string, password: string) {
    return this.post<AccountPayload>('/api/v1/logins', { email, password }).pipe(
      tap((response) => this.storeSessionFromResponse(response, email))
    );
  }

  forgotPassword(email: string) {
    return this.post('/api/v1/logins/forgot-password', { email });
  }

  resetPassword(email: string, passcode: string, password: string) {
    return this.post('/api/v1/logins/reset-password', {
      email,
      passcode,
      password,
    });
  }

  updateName(name: string) {
    return this.patch<AccountRecord>('/api/v1/accounts/me/name', { name }).pipe(
      tap((response) => this.mergeAccountRecord(response.data))
    );
  }

  updateEmail(email: string) {
    return this.patch<AccountRecord>('/api/v1/accounts/me/email', { email }).pipe(
      tap((response) => this.mergeAccountRecord(response.data))
    );
  }

  updatePassword(oldPassword: string, password: string) {
    return this.patch<AccountRecord>('/api/v1/accounts/me/password', {
      oldPassword,
      password,
    });
  }

  updatePlan(monthlyPlanId: number) {
    return this.patch<PlanUpdateResult>('/api/v1/accounts/me/plan', {
      monthlyPlanId,
    });
  }

  createApiKey(description: string, expiresIn: string) {
    return this.post<CreatedApiKey>('/api/v1/api-key', {
      description: description.trim() || undefined,
      expiresIn,
    });
  }

  getApiKeySummary() {
    return this.getStandard<ApiKeySummary>('/api/v1/api-key/summary');
  }

  getServiceStatus() {
    return this.getRaw('/api/v1/status');
  }

  rotateKeyEncryptionKey(body: {
    operationsPassword: string;
    rotationPassword: string;
    force?: boolean;
  }) {
    return this.post('/api/v1/rotate-key-encryption-key', body);
  }

  forceFreshStart(body: {
    operationsPassword: string;
    rotationPassword: string;
  }) {
    return this.post('/api/v1/force-fresh-start', body);
  }

  listWhitelist(
    options: {
      page?: number;
      pageSize?: number;
      email?: string;
      role?: string;
    } = {}
  ) {
    let params = new HttpParams();
    if (options.page != null) {
      params = params.set('page', String(options.page));
    }
    if (options.pageSize != null) {
      params = params.set('pageSize', String(options.pageSize));
    }
    if (options.email?.trim()) {
      params = params.set('email', options.email.trim());
    }
    if (options.role?.trim()) {
      params = params.set('role', options.role.trim());
    }

    return this.http
      .get<WhitelistListResponse>(
        `${environment.encryptionApiUrl}/api/v1/account-whitelist`,
        {
          withCredentials: true,
          params,
        }
      )
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  createWhitelistEntry(body: { email: string; roles: string[] }) {
    return this.post('/api/v1/account-whitelist', body);
  }

  listValidationEmails(
    options: {
      page?: number;
      pageSize?: number;
    } = {}
  ) {
    let params = new HttpParams();
    if (options.page != null) {
      params = params.set('page', String(options.page));
    }
    if (options.pageSize != null) {
      params = params.set('pageSize', String(options.pageSize));
    }

    return this.http
      .get<ValidationEmailListResponse>(
        `${environment.encryptionApiUrl}/api/v1/validation-emails`,
        {
          withCredentials: true,
          params,
        }
      )
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  updateWhitelistEntry(uuid: string, body: { email?: string; roles?: string[] }) {
    return this.patch(`/api/v1/account-whitelist/${uuid}`, body);
  }

  deleteWhitelistEntry(uuid: string) {
    return this.deleteStandard(`/api/v1/account-whitelist/${uuid}`);
  }

  listClientAccounts(
    options: {
      page?: number;
      pageSize?: number;
    } = {}
  ) {
    let params = new HttpParams();
    if (options.page != null) {
      params = params.set('page', String(options.page));
    }
    if (options.pageSize != null) {
      params = params.set('pageSize', String(options.pageSize));
    }

    return this.http
      .get<ClientAccountListResponse>(
        `${environment.encryptionApiUrl}/api/v1/client-accounts`,
        {
          withCredentials: true,
          params,
        }
      )
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  updateClientAccount(uuid: string, body: Record<string, unknown>) {
    return this.patch(`/api/v1/client-accounts/${uuid}`, body);
  }

  listPermissionsCatalog(
    options: {
      page?: number;
      pageSize?: number;
      search?: string;
    } = {}
  ) {
    let params = new HttpParams();
    if (options.page != null) {
      params = params.set('page', String(options.page));
    }
    if (options.pageSize != null) {
      params = params.set('pageSize', String(options.pageSize));
    }
    if (options.search?.trim()) {
      params = params.set('search', options.search.trim());
    }

    return this.http
      .get<PermissionListResponse>(
        `${environment.encryptionApiUrl}/api/v1/permissions`,
        {
          withCredentials: true,
          params,
        }
      )
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  createPermission(name: string) {
    return this.post<CatalogPermission>('/api/v1/permissions', { name });
  }

  updatePermission(uuid: string, name: string) {
    return this.patch<CatalogPermission>(`/api/v1/permissions/${uuid}`, {
      name,
    });
  }

  listRolesCatalog(
    options: {
      page?: number;
      pageSize?: number;
      search?: string;
    } = {}
  ) {
    let params = new HttpParams();
    if (options.page != null) {
      params = params.set('page', String(options.page));
    }
    if (options.pageSize != null) {
      params = params.set('pageSize', String(options.pageSize));
    }
    if (options.search?.trim()) {
      params = params.set('search', options.search.trim());
    }

    return this.http
      .get<RoleListResponse>(`${environment.encryptionApiUrl}/api/v1/roles`, {
        withCredentials: true,
        params,
      })
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  createRole(body: { name: string; permissions: string[] }) {
    return this.post<CatalogRole>('/api/v1/roles', body);
  }

  updateRole(
    uuid: string,
    body: { name?: string; permissions?: string[] }
  ) {
    return this.patch<CatalogRole>(`/api/v1/roles/${uuid}`, body);
  }

  getAccountPermissions(accountUuid: string) {
    return this.getStandard(`/api/v1/client-accounts/${accountUuid}/permissions`);
  }

  replaceAccountRoles(
    accountUuid: string,
    roles: string[],
    operationsPassword?: string
  ) {
    return this.put(`/api/v1/client-accounts/${accountUuid}/roles`, {
      roles,
      operationsPassword: operationsPassword || undefined,
    });
  }

  addAccountRole(
    accountUuid: string,
    roleUuid: string,
    operationsPassword?: string
  ) {
    return this.post(`/api/v1/client-accounts/${accountUuid}/roles`, {
      roleUuid,
      operationsPassword: operationsPassword || undefined,
    });
  }

  removeAccountRole(
    accountUuid: string,
    roleUuid: string,
    operationsPassword?: string
  ) {
    return this.deleteStandard(
      `/api/v1/client-accounts/${accountUuid}/roles/${roleUuid}`,
      { operationsPassword: operationsPassword || undefined }
    );
  }
  listApiKeys(options: { page?: number; start?: number } = {}) {
    let params = new HttpParams();
    if (options.start != null) {
      params = params.set('start', String(options.start));
    } else if (options.page != null) {
      params = params.set('page', String(options.page));
    }
    return this.getStandard('/api/v1/api-keys', params);
  }

  getApiKey(keyId: string) {
    return this.getStandard(`/api/v1/api-keys/${keyId}`);
  }

  revokeApiKey(keyId: string, operationsPassword: string) {
    return this.deleteStandard(`/api/v1/api-keys/${keyId}`, {
      operationsPassword,
    });
  }

  getDestructiveOpsPasswordStatus() {
    return this.getStandard<DestructiveOpsPasswordStatus>(
      '/api/v1/accounts/me/destructive-ops-password/status'
    );
  }

  createDestructiveOpsPassword(operationsPassword: string) {
    return this.post('/api/v1/accounts/me/destructive-ops-password', {
      operationsPassword,
    });
  }

  rotateDestructiveOpsPassword(
    oldOperationsPassword: string,
    newOperationsPassword: string
  ) {
    return this.patch('/api/v1/accounts/me/destructive-ops-password', {
      oldOperationsPassword,
      newOperationsPassword,
    });
  }

  refreshAccess(): Observable<AccountInfoPayload | null> {
    if (!this.isAuthenticated()) {
      return of(null);
    }

    return this.http
      .get<AccountInfoPayload>(`${environment.encryptionApiUrl}/api/v1/info`, {
        withCredentials: true,
      })
      .pipe(
        switchMap((response) => {
          // Local profile without a valid API JWT (cookie missing/expired).
          if (!response?.authenticated) {
            this.handleInvalidSession();
            return throwError(
              () => 'Your session has expired. Please log in again.'
            );
          }
          this.mergeAccessFromInfo(response);
          return of(response);
        }),
        catchError((error) => {
          if (
            error?.status === 401 ||
            error?.error?.code === 'INVALID_JWT'
          ) {
            this.handleInvalidSession();
          }
          return throwError(() =>
            typeof error === 'string' ? error : this.messageFromError(error)
          );
        })
      );
  }

  logout() {
    if (!this.isAuthenticated()) {
      this.clearSession();
      return of(null);
    }

    return this.post('/api/v1/logins/logout', {}).pipe(
      catchError(() => of(null)),
      tap(() => this.clearSession())
    );
  }

  defaultDashboardPath() {
    return this.dashboards()[0]?.path ?? '/account/self-service';
  }

  /**
   * Clears the local session and navigates to login.
   * The httpOnly auth cookie is cleared by the API on INVALID_JWT responses.
   */
  handleInvalidSession() {
    this.clearSession();
    if (!this.isBrowser()) {
      return;
    }
    if (!this.router.url.startsWith('/login')) {
      void this.router.navigateByUrl('/login');
    }
  }

  clearSession() {
    this.sessionSignal.set(null);
    if (this.isBrowser()) {
      localStorage.removeItem(this.storageKey);
    }
  }

  getPendingEmail(): string {
    if (!this.isBrowser()) {
      return '';
    }
    return sessionStorage.getItem('hebern.pendingEmail') ?? '';
  }

  rememberPendingEmail(email: string) {
    if (this.isBrowser()) {
      sessionStorage.setItem('hebern.pendingEmail', email.trim().toLowerCase());
    }
  }

  private post<T = unknown>(path: string, body: unknown) {
    return this.http
      .post<ApiResponse<T>>(`${environment.encryptionApiUrl}${path}`, body, {
        headers: this.jsonHeaders(),
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  private getStandard<T = unknown>(path: string, params?: HttpParams) {
    return this.http
      .get<ApiResponse<T>>(`${environment.encryptionApiUrl}${path}`, {
        withCredentials: true,
        params,
      })
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  private getRaw<T = unknown>(path: string) {
    return this.http
      .get<T>(`${environment.encryptionApiUrl}${path}`, {
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  private patch<T = unknown>(path: string, body: unknown) {
    return this.http
      .patch<ApiResponse<T>>(`${environment.encryptionApiUrl}${path}`, body, {
        headers: this.jsonHeaders(),
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  private put<T = unknown>(path: string, body: unknown) {
    return this.http
      .put<ApiResponse<T>>(`${environment.encryptionApiUrl}${path}`, body, {
        headers: this.jsonHeaders(),
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  private deleteStandard<T = unknown>(path: string, body?: unknown) {
    return this.http
      .delete<ApiResponse<T>>(`${environment.encryptionApiUrl}${path}`, {
        headers: body ? this.jsonHeaders() : undefined,
        body,
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => this.messageFromError(error))));
  }

  private jsonHeaders() {
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  private storeSessionFromResponse(
    response: ApiResponse<AccountPayload>,
    email: string
  ) {
    // JWT is set as an httpOnly cookie by the API; only cache the profile locally.
    if (!response.data?.uuid) {
      return;
    }

    const session: AccountSession = {
      createdAt: new Date().toISOString(),
      user: {
        uuid: response.data.uuid,
        name: response.data.name || email,
        email: email.trim().toLowerCase(),
        forgotAccount: !!response.data.forgot_account,
        loginUuid: response.login?.uuid || response.data.login_uuid,
        roles: response.data.roles ?? [],
        permissions: response.data.permissions ?? [],
      },
    };

    this.persistSession(session);
    if (this.isBrowser()) {
      sessionStorage.removeItem('hebern.pendingEmail');
    }
  }

  private mergeAccessFromInfo(info?: AccountInfoPayload) {
    const current = this.sessionSignal();
    if (!current || !info) {
      return;
    }

    const roleCatalog = info.roleCatalog ?? {};
    const permissions = Object.values(roleCatalog).flatMap(
      (role) => role.permissions ?? []
    );
    const next: AccountSession = {
      ...current,
      user: {
        ...current.user,
        roles: info.callerRoles ?? current.user.roles ?? [],
        permissions: [...new Set(permissions)],
      },
    };
    this.persistSession(next);
  }

  private mergeAccountRecord(account?: AccountRecord) {
    if (!account) {
      return;
    }

    const current = this.sessionSignal();
    if (!current) {
      return;
    }

    const next: AccountSession = {
      ...current,
      user: {
        ...current.user,
        name: account.name,
        email: account.email,
      },
    };
    this.persistSession(next);
  }

  private persistSession(session: AccountSession) {
    this.sessionSignal.set(session);
    if (this.isBrowser()) {
      localStorage.setItem(this.storageKey, JSON.stringify(session));
    }
  }

  private restoreSession() {
    if (!this.isBrowser()) {
      return;
    }

    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw) as AccountSession & { token?: string };
      if (parsed?.user?.uuid) {
        // Drop any legacy JWT that was previously stored in localStorage.
        const session: AccountSession = {
          createdAt: parsed.createdAt || new Date().toISOString(),
          user: parsed.user,
        };
        this.persistSession(session);
      } else {
        localStorage.removeItem(this.storageKey);
      }
    } catch {
      localStorage.removeItem(this.storageKey);
    }
  }

  private messageFromError(error: {
    error?: { message?: string; error?: string; details?: string[]; code?: string };
    status?: number;
  }) {
    if (error.status === 0 || !error.status) {
      return 'Unable to connect to the Hebern service. Please try again.';
    }

    const details = error.error?.details?.join(', ');
    return (
      details ||
      error.error?.message ||
      error.error?.error ||
      'Something went wrong. Please try again.'
    );
  }

  private isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
